import React from 'react';
import { NavLink, Outlet, useParams, Link, Navigate } from 'react-router-dom';
import { ChevronRight, LayoutGrid } from 'lucide-react';
import { isTradeVerticalId, TRADE_VERTICAL_LABELS } from './verticalIds';
import { useEquitiesMode, useExecutionFocusNav } from '../../hooks/useEquitiesMode';

const ALL_TABS: { path: string; label: string }[] = [
  { path: 'overview', label: 'Overview' },
  { path: 'autonomy', label: 'Autonomy' },
  { path: 'strategy', label: 'Strategy & War Room' },
  { path: 'connections', label: 'Connections' },
];

const EXECUTION_FOCUS_TABS = ['overview', 'strategy', 'connections'] as const;

function tabClassName(isActive: boolean): string {
  return [
    'inline-flex items-center rounded-md px-3 py-1.5 text-sm font-medium transition-colors border',
    isActive
      ? 'bg-primary text-primary-foreground border-primary shadow-sm'
      : 'bg-card text-muted-foreground border-border hover:text-foreground hover:bg-muted/50',
  ].join(' ');
}

/**
 * Sprint 1 — per-asset shell for autonomous trading: same tabs for stocks, crypto, forex, futures.
 */
export default function VerticalTradingLayout() {
  const { verticalId } = useParams<{ verticalId: string }>();
  const vid = (verticalId || '').toLowerCase();
  const { equities_only: equitiesOnly } = useEquitiesMode();
  const executionFocus = useExecutionFocusNav();

  if (equitiesOnly && vid !== 'stocks') {
    return <Navigate to="/trade/stocks/overview" replace />;
  }

  if (!isTradeVerticalId(vid)) {
    return (
      <div className="space-y-4 max-w-lg">
        <p className="text-destructive font-medium">Unknown vertical: {verticalId}</p>
        <p className="text-sm text-muted-foreground">Use stocks, crypto, forex, or futures.</p>
        <Link to="/verticals" className="text-primary text-sm underline-offset-4 hover:underline">
          Back to asset verticals
        </Link>
      </div>
    );
  }

  const base = `/trade/${vid}`;
  const vertical = vid;
  const tabs = executionFocus
    ? ALL_TABS.filter((t) => EXECUTION_FOCUS_TABS.includes(t.path as (typeof EXECUTION_FOCUS_TABS)[number]))
    : equitiesOnly
      ? ALL_TABS.filter((t) => t.path !== 'autonomy')
      : ALL_TABS;

  return (
    <div className="space-y-6 min-w-0">
      <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
        <Link
          to={executionFocus || equitiesOnly ? '/portfolio' : '/verticals'}
          className="hover:text-foreground inline-flex items-center gap-1"
        >
          <LayoutGrid className="w-4 h-4" />
          {executionFocus || equitiesOnly ? 'Portfolio' : 'Asset verticals'}
        </Link>
        <ChevronRight className="w-4 h-4 opacity-60" />
        <span className="text-foreground font-semibold">{TRADE_VERTICAL_LABELS[vid]}</span>
        <span className="text-xs font-mono opacity-70">({vid})</span>
      </div>

      <div>
        <h1 className="text-2xl font-bold tracking-tight">
          {TRADE_VERTICAL_LABELS[vid]} — autonomous trading
        </h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
          {executionFocus
            ? 'Controller posture, broker connections, and War Room strategy for US equities. Bots, decisions, and reports live in the main sidebar.'
            : 'Manage posture, decisions, performance, and War Room for this asset class. Platform-wide integrations (shared market data, AI keys) remain in Admin; your broker keys live in Settings.'}
        </p>
      </div>

      <nav className="flex flex-wrap gap-2 border-b border-border pb-3" aria-label="Vertical sections">
        {tabs.map((t) => (
          <NavLink key={t.path} to={`${base}/${t.path}`} className={({ isActive }) => tabClassName(isActive)}>
            {t.label}
          </NavLink>
        ))}
      </nav>

      <Outlet context={{ verticalId: vertical }} />
    </div>
  );
}
